import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox as InboxIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  RefreshCw,
  Send,
  Edit2,
  Sparkles,
  X,
  CheckCircle2,
  AlertCircle,
  Lock,
  Unlock,
  Loader2,
  Mail,
  ShieldAlert,
  Search,
  Save,
  Plus,
  Trash2,
  ExternalLink,
  ArrowLeft,
  ChevronDown,
  BellOff,
  Bell,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_META = {
  submission_status: { label: "Submission status", tone: "blue" },
  how_to_submit: { label: "How to submit", tone: "emerald" },
  payment_issue: { label: "Payment issue", tone: "rose" },
  dispute_or_fraud: { label: "Dispute / fraud", tone: "rose" },
  general_question: { label: "General question", tone: "slate" },
  spam_or_irrelevant: { label: "Spam / irrelevant", tone: "slate" },
};

const RISK_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending review" },
  { key: "scheduled", label: "Scheduled" },
  { key: "red", label: "Red — Sensitive" },
  { key: "green", label: "Green — Safe" },
  { key: "duplicate", label: "Duplicates" },
  { key: "sent", label: "Sent" },
];

const TONE_CLASSES = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.round(ms / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch {
    return "";
  }
}

// Bucket label for grouping the inbox by calendar day.
function dateGroupLabel(iso) {
  if (!iso) return "Unknown date";
  try {
    const d = new Date(iso);
    const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
    const today = startOfDay(new Date());
    const that = startOfDay(d);
    const diffDays = Math.round((today - that) / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "Unknown date";
  }
}

// Split a flat (already date-desc sorted) email list into ordered day groups.
function groupByDay(emails) {
  const groups = [];
  let current = null;
  for (const e of emails) {
    const label = dateGroupLabel(e.received_at);
    if (!current || current.label !== label) {
      current = { label, items: [] };
      groups.push(current);
    }
    current.items.push(e);
  }
  return groups;
}

export default function AdminSupportInboxPage() {
  const [tab, setTab] = useState("inbox");
  const [config, setConfig] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get("/admin/support/config");
      setConfig(r.data);
    } catch {
      toast.error("Could not load mailbox config");
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return (
    <div className="px-2 sm:px-3 md:px-6 py-4 md:py-6 space-y-4" data-testid="admin-support-inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Support Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">
            AI-drafted customer replies. Nothing sends without your approval.
          </p>
        </div>
        {config?.locked && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold max-w-xl" data-testid="mailbox-locked-banner">
            <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Mailbox locked after repeated login failures. This happens if the password changed
              OR the email provider temporarily restricted the account (e.g., daily sending limit
              reached). If it's a provider restriction, no action needed — we retry hourly and
              unlock automatically. Or fix the password / wait, then click "Unlock mailbox" in Settings.
            </span>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="support-subtabs">
        {[
          { k: "inbox", label: "Inbox", icon: InboxIcon },
          { k: "auto", label: "Auto-replies", icon: Sparkles },
          { k: "history", label: "History", icon: HistoryIcon },
          { k: "settings", label: "Settings", icon: SettingsIcon },
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`support-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === k
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "inbox" && <InboxTab config={config} onConfigChange={loadConfig} />}
      {tab === "auto" && <AutoRepliesTab config={config} onConfigChange={loadConfig} />}
      {tab === "history" && <HistoryTab />}
      {tab === "settings" && <SettingsTab config={config} onSaved={loadConfig} />}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* INBOX                                                                    */
/* ──────────────────────────────────────────────────────────────────────── */

function InboxTab({ config, onConfigChange }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "pending") params.status = "pending";
      else if (filter === "scheduled") params.status = "scheduled";
      else if (filter === "sent") params.status = "sent";
      else if (filter === "duplicate") params.status = "duplicate";
      else if (filter === "red") params.risk = "red";
      else if (filter === "green") params.risk = "green";
      const r = await api.get("/admin/support/emails", { params });
      setEmails(r.data || []);
    } catch {
      toast.error("Could not load emails");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/support/sync");
      const d = r.data || {};
      if (d.ok) {
        toast.success(`Sync complete — ${d.new || 0} new email${d.new === 1 ? "" : "s"}.`);
        await load();
        onConfigChange();
      } else if (d.locked) {
        toast.error("Mailbox is locked. Re-enter password in Settings.");
        onConfigChange();
      } else {
        toast.error(d.reason || "Sync failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const selected = useMemo(() => emails.find((e) => e.id === selectedId) || null, [emails, selectedId]);

  const mailboxReady = config?.password_set && config?.email_address && !config?.locked;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-1 flex-wrap" data-testid="inbox-filters">
          {RISK_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`inbox-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={sync}
          disabled={syncing || !mailboxReady}
          data-testid="inbox-sync-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>

      {!mailboxReady && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="mailbox-not-configured">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Mailbox not yet configured.</div>
              <div className="text-amber-700 mt-0.5">
                Open the <button onClick={() => {}} className="underline">Settings</button> tab,
                enter your Titan email address and password, save, then click "Test connection".
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-4">
        {/* List — hidden on mobile when an email is selected */}
        <div className={`lg:col-span-5 bg-white rounded-2xl border border-slate-200 overflow-hidden ${selectedId ? "hidden lg:block" : ""}`}>
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
              <div className="mt-2">Loading…</div>
            </div>
          ) : emails.length === 0 ? (
            <div className="p-10 text-center">
              <Mail className="w-10 h-10 mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500 text-sm">No emails match this filter.</p>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto" data-testid="inbox-grouped-list">
              {groupByDay(emails).map((group) => (
                <div key={group.label} data-testid={`inbox-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="sticky top-0 z-10 px-4 py-1.5 bg-slate-50/95 backdrop-blur border-b border-slate-100 text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center justify-between">
                    <span>{group.label}</span>
                    <span className="text-slate-400 normal-case tracking-normal">{group.items.length}</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {group.items.map((e) => (
                      <li
                        key={e.id}
                        data-testid={`inbox-row-${e.id}`}
                        onClick={() => setSelectedId(e.id)}
                        className={`px-4 py-3 cursor-pointer hover:bg-slate-50/60 active:bg-slate-100 ${
                          selectedId === e.id ? "bg-slate-50" : ""
                        } ${e.status === "duplicate" ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900 text-sm truncate">
                              {e.from_name || e.from_email}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{e.subject || "(no subject)"}</div>
                          </div>
                          <RiskPill red={e.is_red} status={e.status} />
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <CategoryTag category={e.category} />
                          {e.gc_reference && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                              {e.gc_reference}
                            </span>
                          )}
                          {e.submission_found && (
                            <span className="text-[10px] text-emerald-700 font-semibold">· DB match</span>
                          )}
                          <span className="text-[10px] text-slate-400 ml-auto">{relTime(e.received_at)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail — full width on mobile when selected, hidden on mobile when nothing selected */}
        <div className={`lg:col-span-7 ${selectedId ? "" : "hidden lg:block"}`}>
          {selected ? (
            <EmailDetail
              email={selected}
              onChanged={load}
              mailboxEmail={config?.email_address}
              pausedSenders={config?.paused_senders || []}
              onConfigChange={onConfigChange}
              onBack={() => setSelectedId(null)}
              onSelect={setSelectedId}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">
              Select an email on the left to see the AI draft and respond.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RiskPill({ red, status }) {
  if (status === "sent")
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-900 text-white">✓ Sent</span>
    );
  if (status === "duplicate")
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">Duplicate</span>
    );
  if (status === "skipped")
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600">Skipped</span>
    );
  if (red)
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500 text-white">● RED</span>
    );
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500 text-white">● GREEN</span>
  );
}

function CategoryTag({ category }) {
  const meta = CATEGORY_META[category] || { label: category, tone: "slate" };
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${TONE_CLASSES[meta.tone]}`}
    >
      {meta.label}
    </span>
  );
}

function ConversationThread({ thread, currentId, onSelect }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-slate-100 bg-slate-50/50" data-testid="conversation-thread">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-slate-100/60"
        data-testid="conversation-toggle"
      >
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          Conversation · {thread.length} messages
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 space-y-2.5 max-h-72 overflow-y-auto">
          {thread.map((m) => (
            <div key={m.id} data-testid={`thread-item-${m.id}`} className="space-y-1.5">
              {/* Inbound customer message */}
              <button
                onClick={() => m.id !== currentId && onSelect?.(m.id)}
                className={`block w-full text-left rounded-xl px-3 py-2 border transition-colors ${
                  m.id === currentId
                    ? "bg-white border-slate-300 ring-1 ring-slate-300"
                    : "bg-white border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-slate-700">{m.from_name || m.from_email}</span>
                  <span className="text-[10px] text-slate-400 ml-auto">{fmtTime(m.received_at)}</span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{m.body || "(empty)"}</div>
              </button>
              {/* Our reply, if one was sent */}
              {m.status === "sent" && m.draft_body && (
                <div className="ml-6 rounded-xl px-3 py-2 bg-slate-900 text-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-emerald-300">✓ Our reply</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{m.sent_at ? fmtTime(m.sent_at) : ""}</span>
                  </div>
                  <div className="text-xs text-slate-200 mt-0.5 line-clamp-3 whitespace-pre-wrap">{m.draft_body}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailDetail({ email, onChanged, mailboxEmail, pausedSenders = [], onConfigChange, onBack, onSelect }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(email.draft_body || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [thread, setThread] = useState([]);
  const isSent = email.status === "sent";
  const isSkipped = email.status === "skipped";
  const isDuplicate = email.status === "duplicate";
  const senderEmail = (email.from_email || "").toLowerCase();
  const isPaused = pausedSenders.map((s) => (s || "").toLowerCase()).includes(senderEmail);

  const togglePause = async () => {
    setPausing(true);
    try {
      await api.put("/admin/support/sender-pause", { email: email.from_email, paused: !isPaused });
      toast.success(isPaused ? "Auto-replies resumed for this sender" : "Auto-replies paused for this sender");
      onConfigChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update sender");
    } finally {
      setPausing(false);
    }
  };

  // Reset draft when switching emails
  useEffect(() => {
    setDraft(email.draft_body || "");
    setEditing(false);
  }, [email.id, email.draft_body]);

  // Load the full conversation thread for this sender + subject.
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/admin/support/emails/${email.id}/thread`)
      .then((r) => { if (!cancelled) setThread(r.data || []); })
      .catch(() => { if (!cancelled) setThread([]); });
    return () => { cancelled = true; };
  }, [email.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/support/emails/${email.id}/draft`, { body: draft });
      toast.success("Draft saved");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (email.edited && !window.confirm("This draft has manual edits — regenerating will discard them. Continue?")) return;
    setRegenerating(true);
    try {
      await api.post(`/admin/support/emails/${email.id}/regenerate`);
      toast.success("Draft regenerated with current persona/templates");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const approve = async () => {
    if (!window.confirm("Send this reply to the customer now?")) return;
    setSending(true);
    try {
      await api.post(`/admin/support/emails/${email.id}/approve`);
      toast.success("Reply sent");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const skip = async () => {
    if (!window.confirm("Skip this email? No reply will be sent.")) return;
    try {
      await api.post(`/admin/support/emails/${email.id}/skip`);
      toast.success("Skipped");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="email-detail">
      {/* Mobile back button */}
      {onBack && (
        <button
          onClick={onBack}
          data-testid="email-detail-back"
          className="lg:hidden inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-slate-700 border-b border-slate-100 w-full text-left hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4" /> Back to inbox
        </button>
      )}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">From</div>
            <div className="font-semibold text-slate-900 text-sm truncate">
              {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}
            </div>
            <div className="text-xs text-slate-500 mt-1">{email.subject || "(no subject)"}</div>
          </div>
          <RiskPill red={email.is_red} status={email.status} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <CategoryTag category={email.category} />
          {email.gc_reference && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-slate-700 border border-slate-200">
              {email.gc_reference}
            </span>
          )}
          {email.submission_found && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              DB match: {email.submission_snapshot?.card_brand || "submission"} · {email.submission_snapshot?.status || ""}
            </span>
          )}
          <span className="text-[10px] text-slate-400 ml-auto">{fmtTime(email.received_at)}</span>
        </div>
        {/* Per-sender auto-reply pause */}
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid="sender-pause-row">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-700">
              {isPaused ? "Auto-replies paused for this sender" : "Auto-replies active for this sender"}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {isPaused
                ? "Their emails are still drafted but always wait for your manual approval."
                : "When the master switch is on, eligible emails from this sender can auto-send."}
            </div>
          </div>
          <button
            onClick={togglePause}
            disabled={pausing}
            data-testid="sender-pause-toggle"
            className={`inline-flex flex-shrink-0 items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 ${
              isPaused
                ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
            }`}
          >
            {pausing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : isPaused ? (
              <Bell className="w-3.5 h-3.5" />
            ) : (
              <BellOff className="w-3.5 h-3.5" />
            )}
            {isPaused ? "Resume auto-reply" : "Pause auto-reply"}
          </button>
        </div>
      </div>

      {email.classifier_reason && (
        <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 text-xs text-slate-600 italic">
          <strong className="not-italic font-semibold">Why this category:</strong> {email.classifier_reason}
        </div>
      )}

      {/* Reference-not-found warning */}
      {email.gc_reference && !email.submission_found && email.category !== "spam_or_irrelevant" && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-200 text-xs text-red-800 flex items-start gap-2" data-testid="ref-not-found-banner">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong className="font-semibold">Reference {email.gc_reference} was NOT found in this environment's database.</strong>{" "}
            The draft may wrongly tell the customer their submission doesn't exist. If you run separate preview/production
            environments, the submission may only exist in the other one — verify before sending. Auto-send is blocked for
            this email.
          </div>
        </div>
      )}

      {/* Conversation thread */}
      {thread.length > 1 && (
        <ConversationThread thread={thread} currentId={email.id} onSelect={onSelect} />
      )}

      {/* Original */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Original message</div>
        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans max-h-60 overflow-y-auto">
          {email.body || "(empty)"}
        </pre>
      </div>

      {/* Duplicate banner */}
      {isDuplicate && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="duplicate-banner">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Same-day duplicate — auto-muted.</div>
            <div>
              This sender already received a reply earlier today, so we skipped drafting to avoid a redundant response.
              Use <span className="font-semibold">Regenerate</span> below if you still want to draft a reply.
            </div>
          </div>
        </div>
      )}

      {/* Red banner */}
      {email.is_red && !isSent && !isSkipped && !isDuplicate && (
        <div className="px-5 py-3 bg-rose-50 border-b border-rose-200 text-xs text-rose-800 flex items-start gap-2" data-testid="red-banner">
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Human review required.</div>
            <div>
              {email.category === "payment_issue" || email.category === "dispute_or_fraud"
                ? "This category always requires manual handling."
                : ""}
              {email.banned_hits?.length > 0 && (
                <> Banned phrase{email.banned_hits.length > 1 ? "s" : ""} detected: <span className="font-mono">{email.banned_hits.join(", ")}</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Draft */}
      {email.category === "spam_or_irrelevant" ? (
        <div className="px-5 py-6 text-center text-sm text-slate-500" data-testid="no-draft-spam">
          No draft generated — this looks like spam or irrelevant content. Skip when ready.
        </div>
      ) : (
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              AI draft {email.edited && <span className="text-amber-600 normal-case ml-1">(edited)</span>}
            </div>
            {!isSent && !isSkipped && !editing && (
              <div className="flex items-center gap-2">
                <button
                  onClick={regenerate}
                  disabled={regenerating}
                  data-testid="draft-regenerate-btn"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                  title="Re-run the AI drafter with the current persona, templates, and DB context"
                >
                  {regenerating ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Sparkles className="w-3 h-3" />
                  )}
                  {regenerating ? "Regenerating…" : "Regenerate"}
                </button>
                <span className="text-slate-300">·</span>
                <button
                  onClick={() => setEditing(true)}
                  data-testid="draft-edit-btn"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
                >
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
              </div>
            )}
          </div>
          {editing ? (
            <>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={10}
                data-testid="draft-edit-textarea"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white outline-none focus:border-slate-400 text-sm font-sans resize-y"
              />
              <div className="mt-2 flex items-center gap-2 justify-end">
                <button
                  onClick={() => { setDraft(email.draft_body || ""); setEditing(false); }}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="draft-save-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save draft
                </button>
              </div>
            </>
          ) : (
            <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans bg-slate-50 rounded-xl p-3.5 border border-slate-100">
              {email.draft_body || "(no draft)"}
            </pre>
          )}
        </div>
      )}

      {/* Action row */}
      {!isSent && !isSkipped && (
        <div className="px-5 py-3 border-t border-slate-100 flex flex-wrap items-center gap-2 justify-end bg-slate-50/60">
          <button
            onClick={skip}
            data-testid="action-skip-btn"
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700"
          >
            <X className="w-3.5 h-3.5" />
            Skip
          </button>
          {email.category !== "spam_or_irrelevant" && (
            <button
              onClick={approve}
              disabled={sending || !email.draft_body || editing}
              data-testid="action-approve-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Approve &amp; Send
            </button>
          )}
        </div>
      )}

      {isSent && (
        <div className="px-5 py-3 border-t border-slate-100 bg-emerald-50 text-xs text-emerald-800 flex items-center gap-2" data-testid="sent-info">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Sent {email.auto_sent ? "automatically" : ""} from{" "}
          <span className="font-semibold">{mailboxEmail || "your mailbox"}</span> on {fmtTime(email.sent_at)}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* HISTORY                                                                  */
/* ──────────────────────────────────────────────────────────────────────── */

function HistoryTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/support/history");
      setLogs(r.data || []);
    } catch {
      toast.error("Could not load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return logs;
    const needle = q.toLowerCase();
    return logs.filter(
      (l) =>
        (l.action || "").toLowerCase().includes(needle) ||
        (l.actor || "").toLowerCase().includes(needle) ||
        JSON.stringify(l.meta || {}).toLowerCase().includes(needle),
    );
  }, [logs, q]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search action, actor, or details…"
            data-testid="history-search"
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm"
          />
        </div>
        <button
          onClick={load}
          data-testid="history-refresh"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No activity yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {filtered.map((l, i) => (
              <li key={l.id || i} className="px-4 py-3" data-testid={`history-row-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">{l.action}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {l.actor || "system"} · {fmtTime(l.ts)}
                      {l.email_id && <> · email <span className="font-mono">{l.email_id.slice(0, 8)}</span></>}
                    </div>
                    {l.meta && Object.keys(l.meta).length > 0 && (
                      <pre className="mt-1.5 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5 whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">
                        {JSON.stringify(l.meta, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* AUTO-REPLIES LOG                                                         */
/* ──────────────────────────────────────────────────────────────────────── */

function AutoRepliesTab({ config, onConfigChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/support/auto-replies");
      setItems(r.data || []);
      onConfigChange?.();
    } catch {
      toast.error("Could not load auto-reply log");
    } finally {
      setLoading(false);
    }
  }, [onConfigChange]);

  useEffect(() => { load(); }, [load]);

  const cap = config?.auto_send_daily_cap ?? 80;
  const usedToday = config?.auto_sends_today ?? 0;
  const queued = config?.scheduled_count ?? 0;
  const capHit = usedToday >= cap;
  const locked = !!config?.locked;
  const lastErr = config?.last_send_error;
  const lastSentAt = config?.last_auto_sent_at;
  // Only surface a send error if it happened AFTER the last successful send.
  const errIsFresh = lastErr?.ts && (!lastSentAt || lastErr.ts > lastSentAt);
  const fmtT = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-3" data-testid="auto-replies-tab">
      {/* Daily quota / health banner */}
      {config && (
        <div
          data-testid="auto-send-quota-banner"
          className={`px-4 py-3 rounded-xl border text-sm space-y-1 ${
            locked || errIsFresh
              ? "bg-rose-50 border-rose-200 text-rose-800"
              : capHit
              ? "bg-amber-50 border-amber-200 text-amber-800"
              : "bg-emerald-50 border-emerald-200 text-emerald-800"
          }`}
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-bold">
              {locked
                ? "⛔ Mailbox locked — sending suspended"
                : errIsFresh
                ? "⚠️ Last send attempt FAILED"
                : capHit
                ? "⏸ Daily auto-send limit reached"
                : "✓ Auto-send active"}
            </span>
            <span><span className="font-mono font-bold">{usedToday}/{cap}</span> sent today</span>
            {queued > 0 && <span><span className="font-mono font-bold">{queued}</span> queued</span>}
            {queued > 0 && config.next_scheduled_at && (
              <span className="text-xs">next due {fmtT(config.next_scheduled_at)}</span>
            )}
            {lastSentAt && <span className="text-xs">last sent {fmtT(lastSentAt)}</span>}
          </div>
          {locked && (
            <div className="text-xs">
              Reason: {config.lock_reason || "repeated login/send failures"}. This usually means the
              email provider (Titan) hit its OWN daily sending limit — we retry hourly and unlock
              automatically. You can also click "Unlock mailbox" in Settings once the provider resets.
            </div>
          )}
          {!locked && errIsFresh && (
            <div className="text-xs font-mono">
              {fmtT(lastErr.ts)} — {lastErr.error}
            </div>
          )}
          {!locked && errIsFresh && (
            <div className="text-xs">
              If this mentions rate/limit/blocked, the provider (Titan) is refusing sends — queued
              replies retry automatically. Consider lowering the daily limit below the provider's allowance.
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Every reply the AI sent automatically — no manual approval. {items.length} total.
        </p>
        <button
          onClick={load}
          data-testid="auto-replies-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500 text-sm">No auto-replies sent yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              They appear here once the master switch is on and an eligible email auto-sends.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[72vh] overflow-y-auto">
            {items.map((e) => (
              <li key={e.id} data-testid={`auto-reply-row-${e.id}`} className="px-4 py-3">
                <button
                  onClick={() => setOpenId(openId === e.id ? null : e.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 flex-shrink-0">
                          ✓ Auto-sent
                        </span>
                        <span className="font-semibold text-slate-900 text-sm truncate">
                          {e.from_name ? `${e.from_name} <${e.from_email}>` : e.from_email}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">{e.subject || "(no subject)"}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <CategoryTag category={e.category} />
                        {e.gc_reference && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                            {e.gc_reference}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] text-slate-400">{fmtTime(e.sent_at)}</div>
                      <ChevronDown className={`w-4 h-4 text-slate-400 inline-block mt-1 transition-transform ${openId === e.id ? "rotate-180" : ""}`} />
                    </div>
                  </div>
                </button>
                {openId === e.id && (
                  <pre className="mt-2.5 whitespace-pre-wrap text-xs text-slate-700 font-sans bg-slate-50 rounded-xl p-3 border border-slate-100 max-h-72 overflow-y-auto" data-testid={`auto-reply-body-${e.id}`}>
                    {e.draft_body || "(empty)"}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* SETTINGS                                                                 */
/* ──────────────────────────────────────────────────────────────────────── */

function SettingsTab({ config, onSaved }) {
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState("");
  const [savingMailbox, setSavingMailbox] = useState(false);
  const [savingPersona, setSavingPersona] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [savingBanned, setSavingBanned] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [savingTg, setSavingTg] = useState(false);
  const [testingTg, setTestingTg] = useState(false);
  const [whStatus, setWhStatus] = useState(null);
  const [whBusy, setWhBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [newBanned, setNewBanned] = useState("");

  useEffect(() => {
    if (config) setForm({ ...config });
  }, [config]);

  useEffect(() => {
    let alive = true;
    api.get("/admin/support/telegram-webhook")
      .then((r) => { if (alive) setWhStatus(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!form) {
    return (
      <div className="p-10 text-center">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
      </div>
    );
  }

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const saveMailbox = async () => {
    setSavingMailbox(true);
    try {
      const payload = {
        email_address: form.email_address,
        imap_host: form.imap_host,
        imap_port: Number(form.imap_port) || 993,
        imap_ssl: !!form.imap_ssl,
        smtp_host: form.smtp_host,
        smtp_port: Number(form.smtp_port) || 465,
        smtp_ssl: !!form.smtp_ssl,
        poll_interval_minutes: Number(form.poll_interval_minutes) || 5,
      };
      if (password.trim()) payload.password = password.trim();
      const r = await api.put("/admin/support/config", payload);
      setForm({ ...r.data });
      setPassword("");
      toast.success("Mailbox settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingMailbox(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/support/test");
      setTestResult(r.data);
      if (r.data.imap && r.data.smtp) toast.success("Both IMAP and SMTP connections OK");
      else toast.error("Connection test failed — see details below");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const unlock = async () => {
    setUnlocking(true);
    try {
      await api.post("/admin/support/unlock");
      toast.success("Mailbox unlocked");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    } finally {
      setUnlocking(false);
    }
  };

  const savePersona = async () => {
    setSavingPersona(true);
    try {
      const r = await api.put("/admin/support/config", {
        persona_name: form.persona_name,
        persona_title: form.persona_title,
        persona_company: form.persona_company,
        submission_url: form.submission_url,
      });
      setForm({ ...r.data });
      toast.success("Persona saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPersona(false);
    }
  };

  const saveAuto = async () => {
    setSavingAuto(true);
    try {
      const dmin = Math.max(0, Number(form.auto_send_delay_min_minutes ?? 5));
      const dmax = Math.max(dmin, Number(form.auto_send_delay_max_minutes ?? 10));
      const r = await api.put("/admin/support/config", {
        auto_send_master: !!form.auto_send_master,
        auto_send_categories: form.auto_send_categories || {},
        auto_send_delay_min_minutes: dmin,
        auto_send_delay_max_minutes: dmax,
        auto_send_daily_cap: Math.min(300, Math.max(1, Number(form.auto_send_daily_cap ?? 80))),
      });
      setForm({ ...r.data });
      toast.success("Auto-send settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAuto(false);
    }
  };

  const saveBanned = async () => {
    setSavingBanned(true);
    try {
      const r = await api.put("/admin/support/config", { banned_phrases: form.banned_phrases || [] });
      setForm({ ...r.data });
      toast.success("Banned phrases saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingBanned(false);
    }
  };

  const saveTemplates = async () => {    setSavingTpl(true);
    try {
      const r = await api.put("/admin/support/config", { templates: form.templates || {} });
      setForm({ ...r.data });
      toast.success("Templates saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingTpl(false);
    }
  };

  const saveTelegram = async () => {
    setSavingTg(true);
    try {
      const r = await api.put("/admin/support/config", {
        telegram_alerts_enabled: !!form.telegram_alerts_enabled,
        telegram_alert_level: form.telegram_alert_level || "high",
      });
      setForm({ ...r.data });
      toast.success("Alert settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingTg(false);
    }
  };

  const testTelegram = async () => {
    setTestingTg(true);
    try {
      const r = await api.post("/admin/support/telegram-test");
      if (r.data.ok) toast.success("Test alert sent — check your Telegram!");
      else toast.error(r.data.reason || "Could not send test alert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestingTg(false);
    }
  };

  const loadWebhook = async () => {
    try {
      const r = await api.get("/admin/support/telegram-webhook");
      setWhStatus(r.data);
    } catch {
      setWhStatus(null);
    }
  };

  const connectWebhook = async () => {
    setWhBusy(true);
    try {
      const r = await api.post("/admin/support/telegram-webhook");
      if (r.data.ok) toast.success("Two-way replies connected!");
      else toast.error(r.data.reason || "Could not connect");
      await loadWebhook();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    } finally {
      setWhBusy(false);
    }
  };

  const disconnectWebhook = async () => {
    setWhBusy(true);
    try {
      await api.delete("/admin/support/telegram-webhook");
      toast.success("Two-way replies disconnected");
      await loadWebhook();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setWhBusy(false);
    }
  };

  const addBanned = () => {
    const p = newBanned.trim();
    if (!p) return;
    const next = [...(form.banned_phrases || [])];
    if (!next.includes(p)) next.push(p);
    update("banned_phrases", next);
    setNewBanned("");
  };

  const removeBanned = (p) => {
    update("banned_phrases", (form.banned_phrases || []).filter((x) => x !== p));
  };

  return (
    <div className="space-y-5">
      {/* Mailbox */}
      <Card title="Mailbox" subtitle="Connect to Titan (or any IMAP/SMTP server). Password is encrypted at rest." testid="settings-mailbox-card">
        <Grid>
          <Field label="Email address">
            <input
              type="email"
              value={form.email_address || ""}
              onChange={(e) => update("email_address", e.target.value)}
              data-testid="settings-email"
              className={inputCls}
              placeholder="support@gcspark.com"
            />
          </Field>
          <Field label={form.password_set ? "Password (already set — leave blank to keep)" : "Password"}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="settings-password"
              className={inputCls}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </Field>
          <Field label="IMAP host">
            <input value={form.imap_host || ""} onChange={(e) => update("imap_host", e.target.value)} className={inputCls} />
          </Field>
          <Field label="IMAP port">
            <input type="number" value={form.imap_port || 993} onChange={(e) => update("imap_port", e.target.value)} className={inputCls} />
          </Field>
          <Field label="SMTP host">
            <input value={form.smtp_host || ""} onChange={(e) => update("smtp_host", e.target.value)} className={inputCls} />
          </Field>
          <Field label="SMTP port">
            <input type="number" value={form.smtp_port || 465} onChange={(e) => update("smtp_port", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Auto-poll every (minutes)">
            <input
              type="number"
              min={1}
              max={60}
              value={form.poll_interval_minutes || 5}
              onChange={(e) => update("poll_interval_minutes", e.target.value)}
              className={inputCls}
              data-testid="settings-poll-interval"
            />
          </Field>
          <Field label="SSL/TLS">
            <div className="flex items-center gap-4 h-11">
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.imap_ssl} onChange={(e) => update("imap_ssl", e.target.checked)} />
                IMAP SSL
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.smtp_ssl} onChange={(e) => update("smtp_ssl", e.target.checked)} />
                SMTP SSL
              </label>
            </div>
          </Field>
        </Grid>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button onClick={saveMailbox} disabled={savingMailbox} data-testid="settings-save-mailbox" className={btnPrimary}>
            {savingMailbox ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button onClick={test} disabled={testing || !form.password_set} data-testid="settings-test-conn" className={btnSecondary}>
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Test connection
          </button>
          {form.locked && (
            <button onClick={unlock} disabled={unlocking} data-testid="settings-unlock" className={btnSecondary}>
              {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
              Unlock mailbox
            </button>
          )}
          {form.last_sync_at && (
            <span className="text-xs text-slate-500 ml-2">Last sync: {fmtTime(form.last_sync_at)} ({form.last_sync_count || 0} new)</span>
          )}
        </div>
        {testResult && (
          <div className="mt-3 text-xs grid sm:grid-cols-2 gap-2" data-testid="test-result">
            <div className={`p-2.5 rounded-lg border ${testResult.imap ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <div className="font-semibold">{testResult.imap ? "✓ IMAP OK" : "✗ IMAP failed"}</div>
              {testResult.imap_error && <div className="text-rose-700 mt-1 font-mono">{testResult.imap_error}</div>}
            </div>
            <div className={`p-2.5 rounded-lg border ${testResult.smtp ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <div className="font-semibold">{testResult.smtp ? "✓ SMTP OK" : "✗ SMTP failed"}</div>
              {testResult.smtp_error && <div className="text-rose-700 mt-1 font-mono">{testResult.smtp_error}</div>}
            </div>
          </div>
        )}
      </Card>

      {/* Persona */}
      <Card title="Persona &amp; signature" subtitle="The signature block used in every reply." testid="settings-persona-card">
        <Grid>
          <Field label="Support name">
            <input value={form.persona_name || ""} onChange={(e) => update("persona_name", e.target.value)} className={inputCls} data-testid="settings-persona-name" />
          </Field>
          <Field label="Title">
            <input value={form.persona_title || ""} onChange={(e) => update("persona_title", e.target.value)} className={inputCls} data-testid="settings-persona-title" />
          </Field>
          <Field label="Company">
            <input value={form.persona_company || ""} onChange={(e) => update("persona_company", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Submission URL">
            <input value={form.submission_url || ""} onChange={(e) => update("submission_url", e.target.value)} className={inputCls} data-testid="settings-submission-url" />
          </Field>
        </Grid>
        <div className="mt-3">
          <button onClick={savePersona} disabled={savingPersona} className={btnPrimary}>
            {savingPersona ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save persona
          </button>
        </div>
      </Card>

      {/* Auto-send whitelist */}
      <Card title="Auto-send whitelist" subtitle="When the master switch is ON, the categories you tick below are answered automatically. Payment/fraud emails auto-send an acknowledgment only (no amounts, dates, refunds, or admissions). Any draft that trips a banned phrase is held for you regardless." testid="settings-auto-card">
        <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50/50 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.auto_send_master}
            onChange={(e) => update("auto_send_master", e.target.checked)}
            data-testid="settings-auto-master"
            className="w-4 h-4"
          />
          <div>
            <div className="font-semibold text-slate-900 text-sm">Master switch</div>
            <div className="text-xs text-slate-500">Globally enable auto-send. Off = every email requires your manual approval.</div>
          </div>
        </label>
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {["submission_status", "how_to_submit", "general_question", "payment_issue", "dispute_or_fraud"].map((c) => (
            <label key={c} className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={!!(form.auto_send_categories || {})[c]}
                onChange={(e) =>
                  update("auto_send_categories", { ...(form.auto_send_categories || {}), [c]: e.target.checked })
                }
                data-testid={`settings-auto-${c}`}
                disabled={!form.auto_send_master}
                className="w-4 h-4"
              />
              <span className="text-sm font-semibold text-slate-700">{CATEGORY_META[c].label}</span>
            </label>
          ))}
        </div>
        {/* Pacing & daily limit */}
        <div className="mt-4 pt-4 border-t border-slate-200" data-testid="auto-send-pacing">
          <div className="font-semibold text-slate-900 text-sm mb-1">Auto-send pacing &amp; daily limit</div>
          <p className="text-xs text-slate-500 mb-3">
            Replies wait a random human-like delay before sending. The daily limit protects your
            mailbox from provider bans — once hit, queued replies pause until midnight UTC.
            {typeof form.auto_sends_today === "number" && (
              <> Today: <span className="font-mono font-bold">{form.auto_sends_today}/{form.auto_send_daily_cap ?? 80}</span> sent{form.scheduled_count ? <>, <span className="font-mono font-bold">{form.scheduled_count}</span> queued</> : null}.</>
            )}
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-lg">
            <Field label="Delay min (min)">
              <input
                type="number" min={0} max={120}
                value={form.auto_send_delay_min_minutes ?? 5}
                onChange={(e) => update("auto_send_delay_min_minutes", e.target.value)}
                data-testid="settings-delay-min"
                className={inputCls}
              />
            </Field>
            <Field label="Delay max (min)">
              <input
                type="number" min={0} max={120}
                value={form.auto_send_delay_max_minutes ?? 10}
                onChange={(e) => update("auto_send_delay_max_minutes", e.target.value)}
                data-testid="settings-delay-max"
                className={inputCls}
              />
            </Field>
            <Field label="Daily limit (1–300)">
              <input
                type="number" min={1} max={300}
                value={form.auto_send_daily_cap ?? 80}
                onChange={(e) => update("auto_send_daily_cap", e.target.value)}
                data-testid="settings-daily-cap"
                className={inputCls}
              />
            </Field>
          </div>
        </div>
        <div className="mt-3">
          <button onClick={saveAuto} disabled={savingAuto} className={btnPrimary} data-testid="settings-save-auto">
            {savingAuto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </Card>

      {/* Frustrated-customer Telegram alerts */}
      <Card title="Frustrated-customer phone alerts" subtitle="Get an instant Telegram ping on your phone whenever a customer sounds upset, so you can jump in fast." testid="settings-telegram-card">
        <div className={`mb-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${form.telegram_configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="telegram-status">
          {form.telegram_configured ? "✓ Telegram connected" : "⚠ Telegram not connected — add bot credentials on the server"}
        </div>
        <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50/50 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.telegram_alerts_enabled}
            onChange={(e) => update("telegram_alerts_enabled", e.target.checked)}
            data-testid="settings-telegram-enabled"
            className="w-4 h-4"
          />
          <div>
            <div className="font-semibold text-slate-900 text-sm">Enable frustrated-customer alerts</div>
            <div className="text-xs text-slate-500">When on, we notify you the moment an upset customer emails in.</div>
          </div>
        </label>
        <div className="mt-3">
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Alert sensitivity</label>
          <select
            value={form.telegram_alert_level || "high"}
            onChange={(e) => update("telegram_alert_level", e.target.value)}
            disabled={!form.telegram_alerts_enabled}
            data-testid="settings-telegram-level"
            className={inputCls}
          >
            <option value="high">High signal — only clearly angry or dispute/fraud emails (fewer pings)</option>
            <option value="any">Sensitive — any frustrated tone or payment/dispute email (more pings)</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button onClick={saveTelegram} disabled={savingTg} className={btnPrimary} data-testid="settings-save-telegram">
            {savingTg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button onClick={testTelegram} disabled={testingTg || !form.telegram_configured} className={btnSecondary} data-testid="settings-test-telegram">
            {testingTg ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Send test alert
          </button>
        </div>

        {/* Two-way replies */}
        <div className="mt-5 pt-4 border-t border-slate-200" data-testid="telegram-twoway">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold text-slate-900 text-sm">Answer chats from Telegram (two-way)</div>
              <div className="text-xs text-slate-500 max-w-xl mt-0.5">
                When a customer asks for a human or sounds angry, you get the chat in Telegram. <b>Reply once</b> to
                pick the customer, then just keep typing — every message goes straight to their chat on the website.
                Connect once per environment.
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${whStatus?.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}
              data-testid="telegram-twoway-status"
            >
              {whStatus?.active ? "✓ Connected" : "Not connected"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={connectWebhook} disabled={whBusy || !form.telegram_configured} className={btnPrimary} data-testid="settings-connect-twoway">
              {whBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {whStatus?.active ? "Reconnect" : "Connect two-way replies"}
            </button>
            {whStatus?.active && (
              <button onClick={disconnectWebhook} disabled={whBusy} className={btnSecondary} data-testid="settings-disconnect-twoway">
                Disconnect
              </button>
            )}
          </div>
          {whStatus?.last_error_message && (
            <div className="mt-2 text-xs text-rose-700">Telegram error: {whStatus.last_error_message}</div>
          )}
        </div>
      </Card>

      {/* Banned phrases */}
      <Card title="Banned phrases" subtitle="Any draft containing one of these is forced to RED and cannot auto-send." testid="settings-banned-card">        <div className="flex flex-wrap gap-1.5" data-testid="banned-list">
          {(form.banned_phrases || []).map((p) => (
            <span key={p} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-xs font-mono">
              {p}
              <button onClick={() => removeBanned(p)} className="ml-0.5 w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-rose-100">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input
            value={newBanned}
            onChange={(e) => setNewBanned(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBanned(); } }}
            placeholder="add phrase…"
            data-testid="settings-banned-add"
            className={inputCls}
          />
          <button onClick={addBanned} className={btnSecondary}><Plus className="w-4 h-4" /> Add</button>
          <button onClick={saveBanned} disabled={savingBanned} className={btnPrimary} data-testid="settings-save-banned">
            {savingBanned ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </Card>

      {/* Templates */}
      <Card title="Reply templates" subtitle="Soft guidance for the AI per category. Keep them brief and tone-setting." testid="settings-tpl-card">
        <div className="space-y-3">
          {Object.keys(CATEGORY_META).map((c) => (
            <div key={c}>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">{CATEGORY_META[c].label}</div>
              <textarea
                value={(form.templates || {})[c] || ""}
                onChange={(e) => update("templates", { ...(form.templates || {}), [c]: e.target.value })}
                rows={2}
                data-testid={`settings-tpl-${c}`}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm resize-y"
              />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <button onClick={saveTemplates} disabled={savingTpl} className={btnPrimary} data-testid="settings-save-tpl">
            {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save templates
          </button>
        </div>
      </Card>

      {form.password_set && (
        <p className="text-xs text-slate-400 px-1 flex items-center gap-1.5">
          <Lock className="w-3 h-3" /> Password is stored encrypted (Fernet) — never displayed, never logged.
        </p>
      )}
    </div>
  );
}

/* Helper styles & components */
const inputCls = "w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-slate-400 outline-none text-sm";
const btnPrimary = "inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50";
const btnSecondary = "inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 disabled:opacity-50";

function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5" data-testid={testid}>
      <div className="mb-3">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
